import React, { useEffect, useState } from 'react';
import { Card, List, message, Collapse, Drawer, Spin } from 'antd';
import { knowledgeAPI } from '../services/api';
import { KnowledgeItem } from '../types';
import MarkdownRenderer from '../components/MarkdownRenderer';

interface KnowledgeData {
  [category: string]: KnowledgeItem[];
}

interface KnowledgeDetail {
  id: number;
  title: string;
  body: string;
  updated_at: number;
}

export const Knowledge: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [knowledgeData, setKnowledgeData] = useState<KnowledgeData>({});
  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);
  const [selectedKnowledge, setSelectedKnowledge] = useState<KnowledgeDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState<boolean>(false);

  // 获取知识库列表数据
  const fetchKnowledgeList = async () => {
    try {
      setLoading(true);
      const response = await knowledgeAPI.getKnowledge();
      
      setKnowledgeData(response.data.data);
    } catch (error) {
      setKnowledgeData({});
      message.error('获取知识库列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取知识库详情
  const fetchKnowledgeDetail = async (id: number) => {
    try {
      setDetailLoading(true);
      const response = await knowledgeAPI.getKnowledgeDetail(id);
      console.log('Knowledge detail response:', response.data);
      
      setSelectedKnowledge(response.data.data);
      setDrawerVisible(true);
    } catch (error) {
      console.error('Failed to fetch knowledge detail:', error);
      message.error('获取知识库详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  // 处理条目点击
  const handleItemClick = (item: KnowledgeItem) => {
    fetchKnowledgeDetail(item.id);
  };

  // 关闭抽屉
  const handleDrawerClose = () => {
    setDrawerVisible(false);
    setSelectedKnowledge(null);
  };

  useEffect(() => {
    fetchKnowledgeList();
  }, []);

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">使用教程</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
          按分类查看教程，点击条目可打开详情。
        </p>
      </div>
      <Card loading={loading}>
        <Collapse 
          defaultActiveKey={Object.keys(knowledgeData)}
          items={Object.entries(knowledgeData).map(([category, items]) => ({
            key: category,
            label: category,
            children: (
              <List
                itemLayout="vertical"
                dataSource={items}
                renderItem={(item) => (
                  <List.Item
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleItemClick(item)}
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <List.Item.Meta
                      title={<span className="text-blue-600 hover:text-blue-800">{item.title}</span>}
                      description={`更新时间: ${new Date(item.updated_at * 1000).toLocaleDateString()}`}
                    />
                  </List.Item>
                )}
              />
            )
          }))}
        />
      </Card>

      <Drawer
        title={selectedKnowledge?.title || '知识库详情'}
        placement="right"
        size="large"
        onClose={handleDrawerClose}
        open={drawerVisible}
        styles={{
          body: {
            padding: '24px',
            fontSize: '14px',
            lineHeight: '1.6'
          }
        }}
      >
        {detailLoading ? (
          <div className="flex justify-center items-center h-64">
            <Spin size="large" />
          </div>
        ) : selectedKnowledge ? (
          <div>
            <div className="mb-4 text-gray-500 text-sm">
              更新时间: {new Date(selectedKnowledge.updated_at * 1000).toLocaleString()}
            </div>
            <div 
              className="prose prose-sm max-w-none markdown-content"
            >
              <MarkdownRenderer content={selectedKnowledge.body} />
            </div>
          </div>
        ) : (
          <div className="text-center text-gray-500">暂无内容</div>
        )}
      </Drawer>
    </div>
  );
};

export default Knowledge;
